/**
 * Inline BiggerPicture gallery for product images.
 * Creates individual inline galleries for each thumbnail.
 */
document.addEventListener('DOMContentLoaded', function () {
  /**
   * Waits for BiggerPicture to be available before initializing galleries.
   * @param {Object} options - Configuration options.
   * @param {number} options.maxAttempts - Maximum number of attempts to check for BiggerPicture.
   * @param {number} options.interval - Interval between attempts in milliseconds.
   * @returns {Promise} - Resolves when BiggerPicture is available.
   */
  const waitForBiggerPicture = ({ maxAttempts = 50, interval = 100 }) => {
    return new Promise((resolve, reject) => {
      let attempts = 0;

      const checkBiggerPicture = () => {
        attempts++;

        if (typeof window.BiggerPicture !== 'undefined') {
          resolve(true);
        } else if (attempts >= maxAttempts) {
          reject(new Error('BiggerPicture not available'));
        } else {
          setTimeout(checkBiggerPicture, interval);
        }
      };

      checkBiggerPicture();
    });
  };

  /**
   * Configuration options for BiggerPicture gallery initialization.
   * @typedef {Object} GalleryConfig
   * @property {string} target - The target container element.
   * @property {Array} items - Array of gallery items.
   * @property {number} scale - Initial scale of the gallery.
   * @property {string} intro - Animation type for gallery opening.
   * @property {boolean} noClose - Whether to prevent gallery closing.
   * @property {boolean} inline - Whether the gallery is inline.
   * @property {number} maxZoom - Maximum zoom level.
   * @property {number} index - Initial slide index.
   * @property {Function} onOpen - Callback function when gallery opens.
   */

  /**
   * Creates a BiggerPicture gallery instance.
   * @param {Object} options - Gallery configuration options.
   * @param {HTMLElement} options.target - The target container element.
   * @returns {Object} BiggerPicture instance.
   */
  const createGallery = ({ target }) => {
    // @ts-expect-error BiggerPicture is available globally
    return window.BiggerPicture({ target });
  };

  /**
   * Opens a gallery with the specified configuration.
   * @param {Object} options - Gallery opening options.
   * @param {Object} options.gallery - The BiggerPicture instance.
   * @param {Array} options.items - Array of gallery items.
   * @param {Function} options.onOpen - Callback function when gallery opens.
   */
  const openGallery = ({ gallery, items, onOpen }) => {
    gallery.open({
      items,
      scale: 1,
      intro: 'fadeup',
      noClose: true,
      inline: true,
      maxZoom: 2,
      index: 0,
      onOpen: (galleryContainer) => {
        galleryContainer.style.position = 'relative';
        galleryContainer.style.width = '100%';
        galleryContainer.style.height = '100%';
        galleryContainer.style.overflow = 'hidden';

        if (typeof onOpen === 'function') {
          onOpen(galleryContainer);
        }
      }
    });
  };

  /**
   * Checks if a media item is a video type.
   * @param {Object} item - The media item to check.
   * @returns {boolean} True if the item is a video.
   */
  const isVideoItem = (item) => {
    return item.type === 'video' || item.type === 'external_video';
  };

  /**
   * Updates the first image in the media gallery when variant changes, robust for BP.
   * @param {Object} options - Update options.
   * @param {string} options.variantImageUrl - The new variant image URL.
   * @param {string} options.variantImageAlt - The new variant image alt text.
   */
  const updateFirstImage = ({ variantImageUrl, variantImageAlt }) => {
    // Early return if no variant image URL is provided.
    if (!variantImageUrl) {
      return;
    }

    // --- Desktop: Update grid image, BP data, and force BP re-render ---
    // 1. Update the grid image.
    /** @type {HTMLImageElement | null} */
    const desktopFirstImage = document.querySelector(
      '.product-media-gallery__desktop .product-media-gallery__thumb--first img'
    );
    if (desktopFirstImage) {
      desktopFirstImage.src = variantImageUrl;
      if (variantImageAlt) desktopFirstImage.alt = variantImageAlt;
    }

    // 2. Update the BP data source (inlineGalleryItems)
    if (window?.inlineGalleryItems?.[0]) {
      window.inlineGalleryItems[0].img = variantImageUrl;
      if (variantImageAlt) window.inlineGalleryItems[0].alt = variantImageAlt;
    }

    // 3. Remove and re-create the inline gallery for the first image.
    /** @type {HTMLElement | null} */
    const firstInlineContainer = document.getElementById('inline-0');
    if (firstInlineContainer) {
      // Remove all children (BP DOM).
      firstInlineContainer.innerHTML = '';
      // Re-create BP instance and open with updated data.
      const newGallery = window.BiggerPicture({ target: firstInlineContainer });
      newGallery.open({
        items: [window.inlineGalleryItems[0]],
        scale: 1,
        intro: 'fadeup',
        noClose: true,
        inline: true,
        maxZoom: 2,
        index: 0,
        onOpen: (galleryContainer) => {
          galleryContainer.style.position = 'relative';
          galleryContainer.style.width = '100%';
          galleryContainer.style.height = '100%';
          galleryContainer.style.overflow = 'hidden';
        }
      });
      // Update the global map.
      if (window?.productMediaGalleries) {
        window.productMediaGalleries.set('gallery-0', newGallery);
      }
    }

    // --- Mobile: Update main image, thumbnail, and BP instance for first slide ---

    // 1. Update the main image in the first Swiper slide.
    /** @type {HTMLImageElement | null} */
    const mobileFirstImage = document.querySelector(
      '.product-media-gallery__mobile .product-media-gallery__slider swiper-slide:first-child .product-media-gallery__image'
    );
    if (mobileFirstImage) {
      mobileFirstImage.src = variantImageUrl;
      if (variantImageAlt) mobileFirstImage.alt = variantImageAlt;
    }

    // 2. Update the corresponding thumbnail image.
    /** @type {HTMLImageElement | null} */
    const mobileFirstThumb = document.querySelector(
      '.product-media-gallery__mobile .product-media-gallery__slider-thumbnails swiper-slide[data-swiper-slide-index="0"] img'
    );
    if (mobileFirstThumb) {
      mobileFirstThumb.src = variantImageUrl;
      if (variantImageAlt) mobileFirstThumb.alt = variantImageAlt;
    }

    // 3. Update the BP data source for mobile.
    if (window?.inlineGalleryItems?.[0]) {
      window.inlineGalleryItems[0].img = variantImageUrl;
      if (variantImageAlt) window.inlineGalleryItems[0].alt = variantImageAlt;
    }

    // 4. Remove and re-create the BP instance for #mobile-inline-0.
    /** @type {HTMLElement | null} */
    const mobileInlineContainer = document.getElementById('mobile-inline-0');
    if (mobileInlineContainer) {
      mobileInlineContainer.innerHTML = '';
      const newMobileGallery = window.BiggerPicture({ target: mobileInlineContainer });
      newMobileGallery.open({
        items: [window.inlineGalleryItems[0]],
        scale: 1,
        intro: 'fadeup',
        noClose: true,
        inline: true,
        maxZoom: 2,
        index: 0,
        onOpen: (galleryContainer) => {
          galleryContainer.style.position = 'relative';
          galleryContainer.style.width = '100%';
          galleryContainer.style.height = '100%';
          galleryContainer.style.overflow = 'hidden';
        }
      });
      // 5. Update the global map.
      if (window.productMediaGalleries) {
        window.productMediaGalleries.set('mobile-gallery-0', newMobileGallery);
      }
    }
  };

  /**
   * Creates a mapping between thumbnails and gallery items based on URLs.
   * @param {Object} options - Mapping options.
   * @param {NodeListOf<HTMLElement>} options.thumbLinks - Array of thumbnail link elements.
   * @param {Object[]} options.galleryItems - Array of gallery items data.
   * @returns {Map} Map of thumbnail href to gallery item.
   */
  const createThumbnailMapping = ({ thumbLinks, galleryItems }) => {
    const mapping = new Map();

    // Create a map of gallery items by their image URL.
    const itemMap = new Map();
    galleryItems.forEach((item, index) => {
      if (!item.img) {
        return;
      }
      // Extract the base URL without query parameters for matching.
      const baseUrl = item.img.split('?')[0];
      itemMap.set(baseUrl, { item, index });
    });

    // Map thumbnails to gallery items.
    thumbLinks.forEach((link) => {
      const href = link.getAttribute('href');
      if (href) {
        // Extract the base URL without query parameters for matching.
        const baseUrl = href.split('?')[0];
        const mappedItem = itemMap.get(baseUrl);

        if (mappedItem) {
          mapping.set(link, mappedItem);
        }
      }
    });

    return mapping;
  };

  /**
   * Initializes desktop galleries for product images.
   * @param {Object} options - Initialization options.
   * @param {NodeListOf<HTMLElement>} options.thumbLinks - Array of thumbnail link elements.
   * @param {Object[]} options.galleryItems - Array of gallery items data.
   * @returns {Map} Map of created gallery instances.
   */
  const initializeDesktopGalleries = ({ thumbLinks, galleryItems }) => {
    const galleries = new Map();
    const thumbnailMapping = createThumbnailMapping({
      thumbLinks,
      galleryItems
    });

    thumbLinks.forEach((link, index) => {
      // Try to get the mapped item first, fallback to data-index.
      const mappedItem = thumbnailMapping.get(link);
      const dataIndex = link.getAttribute('data-index');

      // Determine item index with fallback logic.
      let itemIndex;
      if (mappedItem) {
        itemIndex = mappedItem.index;
      } else if (dataIndex) {
        itemIndex = parseInt(dataIndex, 10);
      } else {
        itemIndex = index;
      }

      // Safely access gallery item.
      let item = null;
      if (mappedItem) {
        item = mappedItem.item;
      } else if (galleryItems && Array.isArray(galleryItems) && itemIndex >= 0 && itemIndex < galleryItems.length) {
        item = galleryItems[`${itemIndex}`];
      }

      const container = document.getElementById(`inline-${itemIndex}`);

      // Early return if container or item doesn't exist.
      if (!container || !item) {
        return;
      }

      // Skip video items for BiggerPicture gallery as they don't work properly.
      if (isVideoItem(item)) {
        return;
      }

      // Skip if the item doesn't have an image URL (shouldn't happen for images).
      if (!item.img) {
        return;
      }

      try {
        container.style.display = 'block';
        const singleItem = [item];

        const gallery = createGallery({ target: container });
        openGallery({ gallery, items: singleItem });

        const galleryKey = `gallery-${itemIndex}`;
        galleries.set(galleryKey, gallery);
      } catch {
        // Silently handle gallery creation errors.
        // Error logging removed to comply with linting rules.
      }
    });

    return galleries;
  };

  /**
   * Initializes mobile galleries for product images.
   * @param {Object} options - Initialization options.
   * @param {HTMLImageElement[]} options.mobileImages - Array of mobile image elements.
   * @param {Object[]} options.galleryItems - Array of gallery items data.
   * @returns {Map} Map of created mobile gallery instances.
   */
  const initializeMobileGalleries = ({ mobileImages, galleryItems }) => {
    const galleries = new Map();

    mobileImages.forEach((mobileImage, index) => {
      // Safely access gallery item by index.
      let item = null;
      if (galleryItems && Array.isArray(galleryItems) && index >= 0 && index < galleryItems.length) {
        item = galleryItems[`${index}`];
      }

      // Early return if item doesn't exist.
      if (!item) {
        return;
      }

      // Skip video items for BiggerPicture gallery as they don't work properly.
      if (isVideoItem(item)) {
        return;
      }

      const mobileContainer = document.getElementById(`mobile-inline-${index}`);

      // Early return if mobile container doesn't exist.
      if (!mobileContainer) {
        return;
      }

      mobileContainer.style.display = 'block';
      const singleItem = [item];

      const gallery = createGallery({ target: mobileContainer });
      openGallery({ gallery, items: singleItem });

      const mobileGalleryKey = `mobile-gallery-${index}`;
      galleries.set(mobileGalleryKey, gallery);
    });

    return galleries;
  };

  /**
   * Initializes video autoplay for desktop grid view.
   * Videos are now handled by the video-lazyload.js component using intersection observer.
   */
  const initializeDesktopVideoAutoplay = () => {
    // Only run on desktop viewport.
    if (window.innerWidth <= 768) {
      return;
    }

    // Find all video elements in the desktop grid.
    /** @type {NodeListOf<HTMLVideoElement>} */
    const desktopVideos = document.querySelectorAll('.product-media-gallery__desktop video');

    desktopVideos.forEach((video) => {
      // Set video properties for autoplay.
      video.loop = true;
      video.muted = true;
      video.controls = true;

      // Add event listener to restart video if it ends.
      video.addEventListener('ended', () => {
        video.currentTime = 0;
        video.play().catch(() => {
          // Silently handle play errors.
        });
      });

      // Start playing the video.
      video.play().catch(() => {
        // Silently handle play errors.
      });
    });
  };

  /** @type {NodeListOf<HTMLElement>} */
  const thumbLinks = document.querySelectorAll('.product-media-gallery__thumb');
  /** @type {NodeListOf<HTMLImageElement>} */
  const mobileImages = document.querySelectorAll('.product-media-gallery__mobile .product-media-gallery__image');

  // Early return if required data is not available.
  if (!window?.inlineGalleryItems?.length || !thumbLinks.length) {
    return;
  }

  // Wait for BiggerPicture to be available before initializing galleries.
  waitForBiggerPicture({ maxAttempts: 50, interval: 100 })
    .then(() => {
      // Initialize desktop galleries.
      const desktopGalleries = initializeDesktopGalleries({
        thumbLinks,
        galleryItems: window.inlineGalleryItems
      });

      // Initialize mobile galleries.
      const mobileGalleries = initializeMobileGalleries({
        mobileImages,
        galleryItems: window.inlineGalleryItems
      });

      // Initialize desktop video autoplay.
      initializeDesktopVideoAutoplay();

      // Combine all galleries for potential future use.
      const allGalleries = new Map([...desktopGalleries, ...mobileGalleries]);
      window.productMediaGalleries = allGalleries;

      // Expose the updateFirstImage function globally for variant changes.
      window.updateProductMediaGalleryFirstImage = updateFirstImage;
    })
    .catch(() => {
      // Silently handle BiggerPicture initialization errors.
    });
});
